import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { cn } from '@/utils/cn'
import { motion } from '@/utils/motion'
import { useReviewKeyboard } from '@/hooks/useReviewKeyboard'
import { ProvenanceMarker } from '@/components/ui/ProvenanceMarker'
import { SourceBadge } from '@/components/ui/SourceBadge'
import { filledFill, focusRing, quietFill, softFill } from '@/components/ui/buttonStyles'
import { posStyle } from '@/components/reading/sentenceAnalyzer/posStyles'
import type { PosKey } from '@/types/sentence'
import type { ReviewCard, ReviewRating, ReviewSense } from '@/types/review'

interface FlashcardProps {
  card: ReviewCard
  onRate: (rating: ReviewRating) => void
  /** Moves on without scoring. Omitted where every card must be rated. */
  onSkip?: () => void
  /** Opens the full word entry. Omitted for cards that have no detail view. */
  onShowDetails?: () => void
  disabled?: boolean
  /** False while a dialog sits above the card, so its keys are not hijacked. */
  keyboardEnabled?: boolean
}

const POS_LABELS: Record<string, string> = {
  noun: 'Noun', verb: 'Verb', adjective: 'Adjective', adverb: 'Adverb',
  pronoun: 'Pronoun', preposition: 'Preposition', conjunction: 'Conjunction',
  'infinitive marker': 'Particle',
}

/**
 * The card's own part of speech, in the vocabulary of the `--color-pos-*` chip
 * tokens the sentence analyser already uses. Anything unrecognised — a phrase, a
 * custom card, a POS this app has no colour for — lands on the neutral chip.
 */
const POS_KEYS: Record<string, PosKey> = {
  noun: 'noun', verb: 'verb', adjective: 'adj', adverb: 'adv',
  pronoun: 'pron', preposition: 'prep', conjunction: 'conj',
  'infinitive marker': 'part',
}

function posKeyFor(card: ReviewCard): PosKey {
  const pos = card.type === 'word' ? card.word.part_of_speech : card.custom_flashcard.part_of_speech
  return POS_KEYS[pos?.toLowerCase() ?? ''] ?? 'part'
}

// Filled weights ramp from "I failed this" to "trivial": danger, neutral, tinted
// emerald, solid emerald. The old tinted outlines were hard to tell apart.
const RATING_CONFIG: { rating: ReviewRating; label: string; style: string }[] = [
  { rating: 1, label: "Didn't know", style: 'bg-btn-danger-bg text-btn-danger-text hover:bg-btn-danger-hover-bg' },
  { rating: 2, label: 'Barely', style: quietFill },
  { rating: 3, label: 'Knew it', style: softFill },
  { rating: 4, label: 'Easy', style: filledFill },
]

function formatInterval(days: number): string {
  if (days < 1) return 'today'
  // Round first: 1.3 days must read "1 day", not "1 days".
  if (days < 30) {
    const rounded = Math.round(days)
    return rounded === 1 ? '1 day' : `${rounded} days`
  }
  if (days < 365) return `${Math.round(days / 30)} mo`
  return `${(days / 365).toFixed(1)} yr`
}

function previewInterval(card: ReviewCard, rating: ReviewRating): string {
  const { ease_factor: ease, interval_days: interval } = card

  if (rating === 1) return 'today'

  let next: number
  if (interval < 1) next = 1
  else if (interval < 6) next = 6
  else next = interval * ease

  if (rating === 2) next *= 0.8
  if (rating === 4) next *= 1.3

  return formatInterval(next)
}

function cardLemma(card: ReviewCard): string {
  return card.type === 'word' ? card.word.lemma : card.custom_flashcard.lemma
}

function cardEyebrow(card: ReviewCard): string {
  if (card.type === 'word') {
    return POS_LABELS[card.word.part_of_speech.toLowerCase()] ?? card.word.part_of_speech
  }
  if (card.custom_flashcard.card_type === 'phrase') {
    const deck = card.custom_flashcard.deck_name
    return deck ? `Phrase · ${deck}` : 'Phrase'
  }
  const { part_of_speech, gender } = card.custom_flashcard
  const pos = part_of_speech ? (POS_LABELS[part_of_speech.toLowerCase()] ?? part_of_speech) : null
  return [pos, gender].filter(Boolean).join(' · ') || 'Custom card'
}

export function Flashcard({
  card, onRate, onSkip, onShowDetails, disabled = false, keyboardEnabled = true,
}: FlashcardProps) {
  const [revealed, setRevealed] = useState(false)
  const innerRef = useRef<HTMLDivElement>(null)
  const promptId = useId()
  const lemma = cardLemma(card)
  const longText = lemma.length > 40
  const pos = posStyle(posKeyFor(card))
  // Both faces share it, so the card keeps its colour through the flip.
  const face = cn(
    'flashcard-face flex flex-col bg-card-raised border border-border-divider',
    'shadow-[0_18px_40px_-24px_rgba(0,0,0,0.55)] overflow-hidden',
  )

  const reveal = useCallback(() => setRevealed(true), [])

  useReviewKeyboard({
    revealed,
    submitting: disabled,
    nestedOpen: !keyboardEnabled,
    onReveal: reveal,
    onRate,
  })

  useEffect(() => {
    if (revealed && innerRef.current) void motion.flip(innerRef.current, true)
  }, [revealed])

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-6">
        <div className="flashcard w-full max-w-md min-h-[22rem] md:min-h-[26rem]">
          <div ref={innerRef} className={cn('flashcard-inner', revealed && 'is-revealed')}>
            <div
              className={cn(face, 'flashcard-front', !revealed && 'cursor-pointer')}
              onClick={revealed ? undefined : reveal}
            >
              <span aria-hidden="true" className={cn('h-1.5 shrink-0', pos.spine)} />
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-8 text-center">
                <PosChip pos={pos} label={cardEyebrow(card)} />
                <h2
                  className={cn(
                    'font-serif font-medium text-heading leading-tight break-words max-w-full',
                    longText ? 'text-2xl sm:text-3xl' : 'text-4xl sm:text-5xl',
                  )}
                >
                  {lemma}
                </h2>
                {!revealed && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); reveal() }}
                    className={cn(
                      'mt-4 px-6 py-2.5 rounded-full text-sm font-normal tracking-wide min-h-11',
                      filledFill,
                      'transition-colors duration-200',
                      focusRing,
                    )}
                  >
                    Show answer
                  </button>
                )}
              </div>
            </div>

            <div className={cn(face, 'flashcard-back')}>
              <span aria-hidden="true" className={cn('h-1.5 shrink-0', pos.spine)} />
              <div className="flex flex-1 flex-col justify-center overflow-y-auto px-6 py-6">
                {revealed && (
                  <div className="space-y-5 text-center">
                    <span className="block font-serif text-lg text-muted">{lemma}</span>
                    {card.type === 'word' ? <WordAnswer card={card} /> : <CustomAnswer card={card} />}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {revealed && onShowDetails && (
          <button
            type="button"
            onClick={onShowDetails}
            className={cn(
              'mt-4 inline-flex items-center min-h-11 rounded-md px-2',
              'text-sm text-muted font-light hover:text-accent transition-colors duration-200',
              focusRing,
            )}
          >
            See details
          </button>
        )}
      </div>

      {revealed && (
        <div className="px-4 pb-6 pt-2">
          {onSkip && (
            <button
              type="button"
              disabled={disabled}
              onClick={onSkip}
              className={cn(
                'flex w-full max-w-md mx-auto mb-2 sm:mb-3 items-center justify-center gap-2',
                'rounded-xl py-3 min-h-11 text-sm font-normal',
                'transition-colors duration-200 disabled:opacity-40',
                focusRing,
                quietFill,
              )}
            >
              <span>Skip</span>
              <span className="text-xs">· strength unchanged</span>
            </button>
          )}
          <p id={promptId} className="mb-3 text-center text-sm text-muted font-light">
            How well did you know it?
          </p>
          <div role="group" aria-labelledby={promptId} className="grid grid-cols-4 gap-2 sm:gap-3 max-w-md mx-auto">
            {RATING_CONFIG.map(({ rating, label, style }) => (
              <button
                key={rating}
                type="button"
                disabled={disabled}
                onClick={() => onRate(rating)}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-xl px-1 py-3 sm:py-4 min-h-11 text-center leading-tight',
                  'text-sm font-normal transition-colors duration-200 disabled:opacity-40',
                  focusRing,
                  style,
                )}
              >
                <span>{label}</span>
                {/* No opacity here: knocking back text that already sits at ~4.8:1 on
                    its fill drops it under AA. Size carries the hierarchy instead. */}
                <span className="text-xs">{previewInterval(card, rating)}</span>
                <kbd className="sr-only">{rating}</kbd>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/** The card's part of speech, in its own colour — the front's only splash of hue. */
function PosChip({ pos, label }: { pos: ReturnType<typeof posStyle>; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-3 py-1 border',
        'font-mono text-[11px] uppercase tracking-widest',
        pos.bg, pos.border, pos.text,
      )}
    >
      {label}
    </span>
  )
}

function WordAnswer({ card }: { card: Extract<ReviewCard, { type: 'word' }> }) {
  const primaryTranslations = card.senses[0]?.translations?.en ?? []
  const multiSense = card.senses.length > 1

  return (
    <>
      {primaryTranslations.length > 0 && (
        <p className="font-serif text-xl sm:text-2xl text-heading font-medium leading-relaxed">
          {primaryTranslations.join(', ')}
        </p>
      )}

      {multiSense && (
        <div className="space-y-3 text-left">
          {card.senses.map((sense, i) => (
            <SenseBlock key={i} sense={sense} index={i + 1} />
          ))}
        </div>
      )}

      {!multiSense && card.senses[0] && <SenseBlock sense={card.senses[0]} index={null} />}
    </>
  )
}

function CustomAnswer({ card }: { card: Extract<ReviewCard, { type: 'custom' }> }) {
  const { translation, example_sentence, notes } = card.custom_flashcard

  return (
    <>
      <p className="font-serif text-xl sm:text-2xl text-heading font-medium leading-relaxed">
        {translation}
      </p>
      {example_sentence && (
        <p className="text-sm text-muted font-light italic leading-relaxed">{example_sentence}</p>
      )}
      {notes && <p className="text-sm text-muted font-light leading-relaxed">{notes}</p>}
      <ProvenanceMarker />
    </>
  )
}

function SenseBlock({ sense, index }: { sense: ReviewSense; index: number | null }) {
  const translations = sense.translations?.en ?? []
  if (!translations.length && !sense.definition && !sense.example_sentence) return null

  return (
    <div className="space-y-1">
      <p className="text-sm text-body font-light leading-relaxed">
        {index !== null && <span className="text-muted mr-1.5">{index}.</span>}
        {translations.length > 0 && <span className="font-medium">{translations.join(', ')}</span>}
        {translations.length > 0 && sense.definition && <span className="text-muted mx-1.5">&mdash;</span>}
        {sense.definition && <span className="text-muted">{sense.definition}</span>}
      </p>
      {sense.example_sentence && (
        <p className={cn('text-sm text-muted font-light italic leading-relaxed', index !== null && 'ml-4')}>
          {sense.example_sentence}
        </p>
      )}
      <SourceBadge source={sense.source} className={cn(index !== null && 'ml-4')} />
    </div>
  )
}
